import { Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import classNames from "classnames";
import { useCallback, useMemo, VFC } from "react";
import { useTranslation } from "react-i18next";
import { UserTableOutlined } from "../../../../components/Primitives/Icons";
import { useScreenSize } from "../../../../hooks/use-screen-size";
import { Colors } from "../../../../style/Colors";
import { TEXT_SEPARATOR } from "../../../../utils/constants";
import { AppTable } from "../../../../components/tables/app-table.component";
import { IPaginationInputMeta } from "../../../../domain/pagination/pagination-meta";
import { RolePom } from "../../../../domain/user/role-pom";
import { IUser, IUserResult } from "../../../../domain/user/user";
import { GroupDetailHeader } from "../group-detail-header.component";
import { HasLoginIndicator } from "../../../../components/user/has-login-indicator.component";

const useStyles = makeStyles()(() => ({
  disabledAddressText: {
    color: Colors.gray,
  },
  icon: {
    width: 35,
    height: 40,
  },
  textAlign: {
    textAlign: "center",
  },
  loginIndicator: {
    marginLeft: 12,
  },
}));

interface IGroupDetailsUsersBaseTableProps {
  userResult: IUserResult | undefined;
  onAddClick?: VoidFunction;
  onRowClicked: (user: IUser | undefined) => void;
  isLoading: boolean;
  paginationInputMeta: IPaginationInputMeta;
  setPaginationInputMeta: (inputMeta: IPaginationInputMeta) => void;
}

export const GroupDetailsUsersBaseTable: VFC<IGroupDetailsUsersBaseTableProps> = (props) => {
  const {
    userResult,
    onRowClicked,
    isLoading,
    paginationInputMeta,
    setPaginationInputMeta,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { isMobile } = useScreenSize();

  const getUserInfoText = useCallback(
    (user: IUser) => {
      const textParts: string[] = [];

      if (user.rolePom != null) {
        const text =
          user.rolePom === RolePom.Announcer
            ? t("basedata.users.edit.pom_role.announcer")
            : t("basedata.users.edit.pom_role.main_contact");
        textParts.push(text);
      }

      return textParts.join(`${TEXT_SEPARATOR} `);
    },
    [t],
  );

  const getUserMiscText = useCallback(
    (user: IUser) => {
      const textParts: string[] = [];

      if (user.email) {
        textParts.push(user.email);
      }
      if (user.telephone) {
        textParts.push(user.telephone);
      }

      return textParts.join(`${TEXT_SEPARATOR} `);
    },
    [],
  );

  const getUserLabelComponent = useCallback(
    (user: IUser) => {
      return (
        <Grid container direction="column">
          <Grid item>
            <Typography
              variant="body1"
              className={classNames({
                [classes.disabledAddressText]: !user.active,
                [classes.textAlign]: isMobile,
              })}
            >
              <b>
                {user.firstName ?? "-"} {user.lastName ?? "-"}{" "}
              </b>
              {getUserInfoText(user)}
              <HasLoginIndicator hasLogin={user.hasLogin} className={classes.loginIndicator} />
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body1"
              className={classNames({
                [classes.disabledAddressText]: !user.active,
                [classes.textAlign]: isMobile,
              })}
            >
              {getUserMiscText(user)}
            </Typography>
          </Grid>
        </Grid>
      );
    },
    [
      classes.disabledAddressText,
      classes.textAlign,
      classes.loginIndicator,
      getUserInfoText,
      getUserMiscText,
      isMobile,
    ],
  );

  const mappedEntries = useMemo(() => {
    const users = userResult?.users;
    if (!users) return [];

    return users.map((user) => {
      return {
        id: user.email,
        labelComponent: getUserLabelComponent(user),
        iconComponent: <UserTableOutlined className={classes.icon} viewBox="0 0 34.933 40" />,
        isActive: user.active,
      };
    });
  }, [classes.icon, getUserLabelComponent, userResult?.users]);

  return (
    <>
      <Grid container direction="column">
        <Grid item>
          <GroupDetailHeader>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item>
                <Typography variant="h1">{t("basedata.users.heading")}</Typography>
              </Grid>
            </Grid>
          </GroupDetailHeader>
        </Grid>
        <Grid item>
          <AppTable
            entries={mappedEntries}
            paginationInputMeta={paginationInputMeta}
            setPaginationInputMeta={setPaginationInputMeta}
            numberOfRecords={userResult?.meta?.numberOfRecords ?? 0}
            onRowClicked={(tableRow) => {
              const user = userResult?.users.find((user) => user.email === tableRow.id);
              onRowClicked(user);
            }}
            isLoading={isLoading}
            noRowsLabel={t("basedata.group.users_empty")}
          />
        </Grid>
      </Grid>
    </>
  );
};
